import { Button } from "devextreme-react/button";
import { Column, DataGrid, Editing, Paging, Scrolling, Selection } from "devextreme-react/data-grid";
import { SelectBox } from "devextreme-react/select-box";
import { TextBox } from "devextreme-react/text-box";
import { Box, Item } from "devextreme-react/box";
import Popup, { ToolbarItem } from "devextreme-react/popup";
import { useRef, useState } from "react";
import { useGetProductLines, useGetProductTypes } from "../../../../api/product";
import { Field } from "../../../../layouts";
import { showErrorMessage } from "../../../../utils/messages";
import { ProductCatalog, ProductLine } from "../../../../types/enums";
import { CreditMemoProductSearchResult } from "../types/CreditMemoProductSearchResult";
import { alert } from "devextreme/ui/dialog";
import { LoadPopupPanel } from "../../../../components";
import { apiSearchCreditMemoProducts } from "../api/search-credit-memo-products";
import { CreditMemoCatalogProduct } from "../types/CreditMemoCatalogProduct";

export default function CreditMemoProductPopup(props: {
	visible: boolean,
	onPopupHidden: () => void,
	onProductsAdded: (newProducts: CreditMemoCatalogProduct[]) => void
}) {

	const [processing, setProcessing] = useState(false);

	const [searchCriteria, setSearchCriteria] = useState<
		{ productLineID: number, productTypeID?: number, sku?: string, productDescription?: string }>(
			{ productLineID: ProductLine.Granite });

	const [searchResults, setSearchResults] = useState<CreditMemoProductSearchResult>();

	const productLines = useGetProductLines(props.visible);
	const productTypes = useGetProductTypes(ProductCatalog.Material, searchCriteria.productLineID, props.visible);

	const searchResultsRef = useRef<DataGrid>(null);

	return (
		<Popup
			title="Add Credit Memo Product"
			visible={props.visible}
			hideOnOutsideClick={true}
			onHiding={() => props.onPopupHidden()}
		>
			<ToolbarItem
				options={{
					icon: 'dx-icon dx-icon-close',
					onClick: function () {
						props.onPopupHidden();
					}
				}}
				widget="dxButton"
				location="after"
			/>
			<Box className="product-search-filter field-label-25">
				<Item ratio={1}>
					<Field label={'Product Line'}>
						<SelectBox elementAttr={{ 'data-testid': 'dd-product-line' }}
							showClearButton={false}
							dataSource={productLines.data}
							valueExpr={'productLineID'}
							displayExpr={'description'}
							value={searchCriteria.productLineID}
							onValueChanged={(e) => {
								setSearchCriteria({ ...searchCriteria, productLineID: e.value });
								setSearchResults({} as CreditMemoProductSearchResult);
							}}
						/>
					</Field>
					<Field label={'Product Type'}>
						<SelectBox elementAttr={{ 'data-testid': 'dd-product-type' }}
							showClearButton={true}
							dataSource={productTypes.data}
							valueExpr={'productTypeID'}
							displayExpr={'description'}
							value={searchCriteria.productTypeID}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, productTypeID: e.value })}
						/>
					</Field>
				</Item>
				<Item ratio={1}>
					<Field label={'SKU'}>
						<TextBox inputAttr={{ 'data-testid': 'txt-sku' }}
							showClearButton={true}
							value={searchCriteria.sku}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, sku: e.value })}
						></TextBox>
					</Field>
					<Field label={'Product Description'}>
						<TextBox inputAttr={{ 'data-testid': 'txt-product-description' }}
							showClearButton={true}
							value={searchCriteria.productDescription}
							onValueChanged={(e) => setSearchCriteria({ ...searchCriteria, productDescription: e.value })}
						></TextBox>
					</Field>
				</Item>
				<Item ratio={0} baseSize={100}>
					<Button elementAttr={{ 'data-testid': 'btn-search' }}
						text="Search"
						onClick={() => {
							setProcessing(true);

							searchResultsRef.current?.instance.clearSelection();

							apiSearchCreditMemoProducts(
								searchCriteria.productLineID,
								searchCriteria.productTypeID,
								searchCriteria.sku,
								searchCriteria.productDescription)
								.then((result) => {
									if (result.isSuccess)
										setSearchResults(result);
									else
										showErrorMessage(result.errorMessage!);
								})
								.finally(() => setProcessing(false));
						}}>
					</Button>
				</Item>
			</Box>

			<DataGrid
				ref={searchResultsRef}
				className="product-search-results"
				dataSource={searchResults?.products}
				height={'76%'}
				allowColumnResizing={true}
				wordWrapEnabled={true}
				keyExpr={['productID']}
				repaintChangesOnly={true}
			>
				<Paging enabled={false}></Paging>
				<Scrolling rowRenderingMode='virtual'></Scrolling>
				<Selection mode="multiple" allowSelectAll={false} />
				<Editing allowUpdating={true} mode={'cell'} refreshMode={'repaint'}></Editing>
				<Column
					dataField="sku"
					caption="House PO SKU"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					dataField="description"
					caption={'Description'}
					dataType="string"
					width={150}
					allowEditing={false}
				/>
				<Column
					dataField="thickness"
					caption="Thickness"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					dataField="productType"
					caption="Product Type"
					dataType="string"
					allowEditing={false}
				/>
				<Column
					dataField={'quantity'}
					caption="Quantity"
					width={100}
					dataType={'number'}
					editorOptions={{ showSpinButtons: true, min: 0, format: "##0.###" }}
					showEditorAlways={true}
				/>
			</DataGrid>

			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					elementAttr: { 'data-testid': 'btn-add-items' },
					text: 'Add Items',
					onClick: () => {
						let isValid = true;

						const selectedRows = searchResultsRef.current?.instance.getSelectedRowsData() as CreditMemoCatalogProduct[];

						if (selectedRows.length === 0) {
							alert("Please select at least one item", "Error");
							isValid = false;
						}

						for (let row of selectedRows) {
							if (row.quantity <= 0) {
								alert("Quantity must be greater than 0", "Error");
								isValid = false;
								break;
							}
						}

						if (isValid) {
							props.onProductsAdded(selectedRows);
							props.onPopupHidden();
							searchResultsRef.current?.instance.clearSelection();
						}
					}
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			>
			</ToolbarItem>
			<ToolbarItem
				options={{
					type: 'normal',
					stylingMode: 'contained',
					elementAttr: { 'data-testid': 'btn-cancel' },
					text: 'Cancel',
					onClick: () => props.onPopupHidden()
				}}
				toolbar={'bottom'}
				widget="dxButton"
				location="after"
			>
			</ToolbarItem>
			<LoadPopupPanel visible={productLines.isLoading || productTypes.isLoading || processing}></LoadPopupPanel>
		</Popup>
	)
}

